import { z } from 'zod';

import { AvailabilityRuleInputSchema, AvailabilityRuleSchema, OperatingHoursSchema } from './availability-rule';
import { ExperienceImageSizeMediaSchema, ExperienceAsResourceSchema, MediaSchema } from './common';
import { ScheduleType } from './schedule-time-frame';
import { ExperienceVersion } from '../../experience/const';
import { buildQueryString } from '../../utils/querystring';
import { LanguageCode } from '../brand/brand';
import { makeEndpoint, paginateQuerySchema, paginateResponseSchema } from '../common';
import { IntegrationMetaSchema, TaxSchema } from '../experience/experiences';
import { PriceTierSchema } from '../experience/price-tier-rules';
import { ShapeSchema } from '../experience/shape';
import { PaymentMethods } from '../payment-method/public';
import { TranslationSchema, TranslationWithIdSchema } from '../translation/types';

export enum NumberPattern {
  numeric = 'numeric',
  alphabetic = 'alphabetic',
}

// Re-exported so every existing import site keeps working; both are declared in a leaf module so the
// pure helpers that need them stay clear of this file's endpoint graph.
export {
  AvailabilityTimeFrame,
  ScheduleType,
  TIME_FRAMES_BY_SCHEDULE_TYPE,
  deriveScheduleTypeForTimeFrame,
  isContinuousScheduleType,
  isDayBasedTimeFrame,
  isDurationBasedTimeFrame,
  isTimeFrameAllowedFor,
} from './schedule-time-frame';
export type { ContinuousScheduleType, DayBasedTimeFrame, DurationBasedTimeFrame } from './schedule-time-frame';
export { AvailabilityRuleInputSchema, AvailabilityRuleSchema, OperatingHoursSchema } from './availability-rule';
export type { AvailabilityRule, AvailabilityRuleInput, OperatingHours } from './availability-rule';

export const ResourceGroupSchema = z.object({
  id: z.string().uuid(),
  order: z.number().finite(),
  resourceGroupCollectionId: z.string().uuid(),
  version: z.nativeEnum(ExperienceVersion),
  pictures: z.array(ExperienceImageSizeMediaSchema).max(3),
  medias: z.array(MediaSchema).max(3),
  title: z.string().trim().nonempty(),
  description: z.string().trim(),
  included: z.array(z.string()),
  experiences: z.array(ExperienceAsResourceSchema),
  defaultShape: ShapeSchema.nullable(),
  numberPattern: z.nativeEnum(NumberPattern),
  /** Pool-backed: each resource sells one unit of this pool, shared with any other group over it. */
  resourcePoolId: z.string().uuid().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const ResourceGroupResponseSchema = ResourceGroupSchema.extend({
  availabilityRule: AvailabilityRuleSchema.nullable().optional(),
  defaultPriceTiers: z.array(PriceTierSchema),
  reminder: z.string(),
  taxes: z.array(TaxSchema.and(z.object({ name: z.string() }))),
  paymentMethods: z.array(z.nativeEnum(PaymentMethods)),
  integrationMeta: z.array(IntegrationMetaSchema),
  providedItems: z.array(z.string()),
  minimumRequiredToBook: z.number().nonnegative().nullable(),
  maximumAllowedToBook: z.number().nonnegative().nullable(),
  ratings: z.object({ rating: z.number().nullable(), recommendability: z.number().nullable(), total: z.number() }),
  isExclusive: z.boolean(),
});
export type ResourceGroupResponse = z.infer<typeof ResourceGroupResponseSchema>;

export const PostResourceGroupEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, resourceGroupCollectionId }: { resourceGroupCollectionId: string; brandId: string }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/resource-groups`,
  requestSchema: ResourceGroupSchema.pick({
    order: true,
    title: true,
    included: true,
    defaultShape: true,
    numberPattern: true,
  })
    .extend({
      // Resource titles are derived as "<group title> #<number>" and experience.title is varchar(128)
      title: ResourceGroupSchema.shape.title.max(120, { message: 'Title must be at most 120 characters long.' }),
      description: ResourceGroupSchema.shape.description.optional(),
      medias: z.array(MediaSchema.omit({ kind: true })).optional(),
      availabilityRule: AvailabilityRuleInputSchema.nullable().optional(),
      resourcePoolId: ResourceGroupSchema.shape.resourcePoolId,
    })
    .strict(),
  responseSchema: ResourceGroupResponseSchema.strict(),
});

export const GetResourceGroupsQuerySchema = paginateQuerySchema().merge(
  z.object({
    filters: z
      .array(
        z.object({
          operator: z.literal('equals'),
          name: z.literal('status'),
          value: z.nativeEnum(ExperienceVersion),
        }),
      )
      .optional(),
    language: z.nativeEnum(LanguageCode).optional(),
    bookingId: z.string().uuid().optional(),
  }),
);
export type GetResourceGroupsQuery = z.infer<typeof GetResourceGroupsQuerySchema>;

export const GetResourceGroupsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    resourceGroupCollectionId,
    query,
  }: {
    resourceGroupCollectionId: string;
    brandId: string;
    query: z.input<typeof GetResourceGroupsQuerySchema>;
  }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/resource-groups?${buildQueryString(
      query,
    )}`,
  requestSchema: z.void(),
  responseSchema: paginateResponseSchema(ResourceGroupResponseSchema).strict(),
  querySchema: GetResourceGroupsQuerySchema,
});

const GetResourceGroupQuerySchema = z.object({
  version: z.nativeEnum(ExperienceVersion).default(ExperienceVersion.LATEST),
});

export const GetResourceGroupEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    resourceGroupCollectionId,
    resourceGroupId,
    query,
  }: {
    resourceGroupCollectionId: string;
    brandId: string;
    resourceGroupId: string;
    query: z.input<typeof GetResourceGroupQuerySchema>;
  }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/resource-groups/${resourceGroupId}?${buildQueryString(
      query,
    )}`,
  requestSchema: z.void(),
  responseSchema: ResourceGroupResponseSchema.strict(),
  querySchema: GetResourceGroupQuerySchema,
});

const ResourceGroupAvailabilityResponseSchema = z.object({
  operatingHours: z.array(OperatingHoursSchema),
});

/** When a resource is open, one row per date range and weekday set. */
export const GetResourceGroupAvailabilityEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, resourceGroupId }: { brandId: string; resourceGroupId: string }) =>
    `/v1/brands/${brandId}/resource-groups/${resourceGroupId}/availability`,
  requestSchema: z.void(),
  responseSchema: ResourceGroupAvailabilityResponseSchema.strict(),
});

/**
 * Adds availability, as many windows at a time as the operator described.
 *
 * A list rather than a single row because a week is usually several windows at once — 10–6 Monday,
 * Wednesday and Friday, 12–6 Tuesday and Thursday, 8–8 at weekends — and those should land together or
 * not at all.
 */
export const PostResourceGroupAvailabilityEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, resourceGroupId }: { brandId: string; resourceGroupId: string }) =>
    `/v1/brands/${brandId}/resource-groups/${resourceGroupId}/availability`,
  requestSchema: z.object({ windows: z.array(OperatingHoursSchema.omit({ createdAt: true, id: true })).min(1) }).strict(),
  responseSchema: ResourceGroupAvailabilityResponseSchema.strict(),
});

/**
 * Changes one availability row in place — its dates, days or hours — keeping its id.
 *
 * In place rather than remove-and-add, so an operator narrowing a season or moving closing time by an
 * hour is editing the row they can see, and the row's own conflicts are judged against everything but
 * itself.
 */
export const PatchResourceGroupAvailabilityEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({
    brandId,
    resourceGroupId,
    operatingHoursId,
  }: {
    brandId: string;
    operatingHoursId: string;
    resourceGroupId: string;
  }) => `/v1/brands/${brandId}/resource-groups/${resourceGroupId}/availability/${operatingHoursId}`,
  requestSchema: OperatingHoursSchema.omit({ createdAt: true, id: true }).strict(),
  responseSchema: OperatingHoursSchema.extend({ id: z.string().uuid() }).strict(),
});

export const DeleteResourceGroupAvailabilityEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({
    brandId,
    resourceGroupId,
    operatingHoursId,
  }: {
    brandId: string;
    operatingHoursId: string;
    resourceGroupId: string;
  }) => `/v1/brands/${brandId}/resource-groups/${resourceGroupId}/availability/${operatingHoursId}`,
  requestSchema: z.void(),
  responseSchema: z.object({ id: z.string().uuid() }).strict(),
});

export const DeleteResourceGroupAvailabilityDatesQuerySchema = z
  .object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
  })
  .strict();
export type DeleteResourceGroupAvailabilityDatesQuery = z.infer<typeof DeleteResourceGroupAvailabilityDatesQuerySchema>;

/**
 * Removes a resource group's availability on a run of dates — one day, a range, or from a day on with no
 * end — across every one of its rows. A row that runs through the dates is split around them, so the
 * days before and after keep their hours. Answers with what availability is left.
 */
export const DeleteResourceGroupAvailabilityDatesEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({
    brandId,
    query,
    resourceGroupId,
  }: {
    brandId: string;
    query: z.input<typeof DeleteResourceGroupAvailabilityDatesQuerySchema>;
    resourceGroupId: string;
  }) => `/v1/brands/${brandId}/resource-groups/${resourceGroupId}/availability?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: ResourceGroupAvailabilityResponseSchema.strict(),
  querySchema: DeleteResourceGroupAvailabilityDatesQuerySchema,
});

export const BookableResourceGroupSchema = z.object({
  collectionTitle: z.string().nullable(),
  id: z.string().uuid(),
  isPromoted: z.boolean(),
  scheduleType: z.nativeEnum(ScheduleType),
  title: z.string(),
});
export type BookableResourceGroup = z.infer<typeof BookableResourceGroupSchema>;

export const GetBookableResourceGroupsQuerySchema = z.object({
  scheduleType: z.nativeEnum(ScheduleType).optional(),
});
export type GetBookableResourceGroupsQuery = z.infer<typeof GetBookableResourceGroupsQuerySchema>;

export const GetBookableResourceGroupsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof GetBookableResourceGroupsQuerySchema> }) =>
    `/v1/brands/${brandId}/resource-groups?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: z.object({ items: z.array(BookableResourceGroupSchema) }).strict(),
  querySchema: GetBookableResourceGroupsQuerySchema,
});

export const PatchResourceGroupEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({
    brandId,
    resourceGroupCollectionId,
    resourceGroupId,
  }: {
    resourceGroupCollectionId: string;
    brandId: string;
    resourceGroupId: string;
  }) => `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/resource-groups/${resourceGroupId}`,
  requestSchema: PostResourceGroupEndpoint.requestSchema.strict(),
  responseSchema: ResourceGroupResponseSchema.strict(),
});

export const DeleteResourceGroupEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({
    brandId,
    resourceGroupCollectionId,
    resourceGroupId,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    resourceGroupId: string;
  }) => `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/resource-groups/${resourceGroupId}`,
  requestSchema: z.void(),
  responseSchema: z.object({
    id: z.string(),
  }),
});

export const GetTranslationsForResourceGroupQuerySchema = z.object({
  language: z.nativeEnum(LanguageCode).optional(),
});

export const GetTranslationsForResourceGroupEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    resourceGroupCollectionId,
    resourceGroupId,
    version,
    query,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    resourceGroupId: string;
    version: ExperienceVersion;
    query: z.input<typeof GetTranslationsForResourceGroupQuerySchema>;
  }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/resource-groups/${resourceGroupId}/version/${version}/i18n?${buildQueryString(
      query,
    )}`,
  responseSchema: z.object({
    translations: z.object({
      resourceGroup: z.array(TranslationWithIdSchema),
      listingAddOns: z.array(TranslationWithIdSchema),
      customQuestions: z.array(TranslationWithIdSchema),
      listingIncluded: z.array(TranslationWithIdSchema),
    }),
  }),
  requestSchema: z.void(),
  querySchema: GetTranslationsForResourceGroupQuerySchema,
});

export const PatchTranslationsForResourceGroupEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({
    brandId,
    resourceGroupCollectionId,
    resourceGroupId,
    version,
  }: {
    resourceGroupId: string;
    version: ExperienceVersion;
    brandId: string;
    resourceGroupCollectionId: string;
  }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/resource-groups/${resourceGroupId}/version/${version}/i18n`,
  responseSchema: z.void(),
  requestSchema: z.object({
    translations: z.array(TranslationSchema.omit({ createdAt: true, updatedAt: true })),
  }),
});

export enum ResourceGroupField {
  TITLE = 'title',
  DESCRIPTION = 'description',
}
